/**
 * Resuming an OAuth authorization after the user has had to sign in.
 *
 * The consent screen at /oauth/authorize is a Rails page, not an SPA route.
 * When a customer's coding agent sends them there and they have no session,
 * Rails bounces them to /auth/signin?redirect_to=<the authorization request>
 * (Oauth::BaseController::SIGN_IN_PATH). That must stay the SPA's real
 * sign-in route: bare /signin is parsed as an org id and AuthenticatedRoute
 * strips the query string on the hop to /auth/signin, so redirect_to is
 * never stashed. This is the pair of functions that carries that target
 * across the sign-in, since the SPA remounts and the query string does not
 * survive it.
 *
 * Mirrors the existing `post_login_redirect` pattern in Root.jsx, which does
 * the same thing for the subscription flow.
 */

const STORAGE_KEY = 'oauth_post_login_redirect';

/** Mirror: SignupContext::STUDIO_OAUTH_CLIENT_ID in app/services/signup_context.rb */
export const STUDIO_OAUTH_CLIENT_ID = 'codalio-studio';

/**
 * Only a same-origin path under /oauth/ is ever stored.
 *
 * This value comes off a query string and ends up in `window.location`, so
 * without this it is an open redirect: any page could send a user to
 * /auth/signin?redirect_to=https://evil.example and have us deliver them there
 * wearing a fresh session. Rejecting anything with a scheme or a host, and
 * anything not under /oauth/, leaves only the one destination this exists for.
 */
export const isResumableOauthPath = (value) => {
  if (typeof value !== 'string' || value === '') return false;
  // A protocol-relative URL (//evil.example) is a host, not a path.
  if (value.startsWith('//')) return false;
  if (!value.startsWith('/oauth/')) return false;
  // Belt and braces: anything the URL parser reads as absolute is not a path.
  try {
    return (
      new URL(value, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
};

/**
 * When the stored path was last written, and how long it still counts as a
 * Studio sign-in in progress. The path itself is only cleared by a completed
 * login, so one abandoned at the sign-in page would otherwise label every later
 * signup from this browser as a Studio signup.
 */
const STORED_AT_KEY = 'oauth_post_login_redirect_at';
const STUDIO_RESUME_MAX_AGE_MS = 60 * 60 * 1000;

// A missing or unparseable timestamp reads as stale.
const storedWithinMaxAge = () => {
  try {
    const age = Date.now() - Number(window.localStorage.getItem(STORED_AT_KEY));
    return age >= 0 && age <= STUDIO_RESUME_MAX_AGE_MS;
  } catch {
    return false;
  }
};

/**
 * Mirror: SignupContext.studio_oauth_resume? in app/services/signup_context.rb
 * (a repeated client_id is not Studio there, so it is not here).
 */
export const isStudioOauthResume = (path) => {
  if (!isResumableOauthPath(path)) return false;
  const query = path.includes('?') ? path.split('?', 2)[1] : '';
  const clientIds = new URLSearchParams(query).getAll('client_id');
  return clientIds.length === 1 && clientIds[0] === STUDIO_OAUTH_CLIENT_ID;
};

/** Valid pending authorize path for Codalio Studio, written within the last hour, or null. */
export const studioOauthResumePath = () => {
  const stored = peekOauthRedirect();
  if (!stored || !isStudioOauthResume(stored)) return null;

  return storedWithinMaxAge() ? stored : null;
};

/** Reads the stored OAuth resume without clearing it (signup must not consume). */
export const peekOauthRedirect = () => {
  let stored = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  return isResumableOauthPath(stored) ? stored : null;
};

/** Remembers where to return once the user is signed in. */
export const rememberOauthRedirect = (value) => {
  if (!isResumableOauthPath(value)) return false;

  try {
    // SignInPageWrapper calls this on every render; re-stamping an unchanged,
    // still-fresh path would slide the one-hour window while the page is open.
    const unchanged =
      window.localStorage.getItem(STORAGE_KEY) === value &&
      storedWithinMaxAge();
    window.localStorage.setItem(STORAGE_KEY, value);
    if (!unchanged) {
      window.localStorage.setItem(STORED_AT_KEY, String(Date.now()));
    }
    return true;
  } catch {
    // Private browsing, or storage disabled. The user still signs in; they
    // just land on their dashboard instead of back at the consent screen.
    return false;
  }
};

/**
 * When this tab last sent the user back to an authorization request, and how
 * long afterwards a bounce straight back to sign-in counts as a loop (see
 * rememberOauthRedirectUnlessLooping). sessionStorage: it is about this tab's
 * redirect chain, not the browser.
 *
 * Every resume sets it, a normal signed-out sign-in included, but only the
 * signed-in stash honours it: a signed-in retry in the same tab within the
 * window lands on the dashboard on purpose. The sign-in page's own stash does
 * not check it, so a signed-out sign-in is never declined.
 */
const RESUMED_AT_KEY = 'oauth_resume_attempted_at';
const RESUME_LOOP_WINDOW_MS = 15 * 1000;

/**
 * Takes the stored target, if there is one, and clears it.
 *
 * Re-validated on the way out as well as in: what is read back has been
 * outside our control in browser storage, and a stale value from an older
 * build should not be trusted just because we wrote the key.
 */
export const consumeOauthRedirect = () => {
  let stored = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STORED_AT_KEY);
  } catch {
    return null;
  }

  if (!isResumableOauthPath(stored)) return null;

  try {
    window.sessionStorage.setItem(RESUMED_AT_KEY, String(Date.now()));
  } catch {
    // No guard without storage; the resume itself still happens.
  }
  return stored;
};

/**
 * `rememberOauthRedirect` for a visitor who may already be signed in.
 *
 * Rails recognises the user by a cookie that lapses after a day, while the
 * SPA's own session lasts weeks. A signed-in user whose cookie lapsed is sent
 * to /auth/signin?redirect_to=... and then straight on to their dashboard by
 * NonAuthenticatedRoute, before the sign-in page could remember where they
 * were going, so the app that started the flow waits forever. Remembering it
 * first lets the dashboard send them back, now with a fresh cookie (the session
 * check that proved they were signed in re-set it).
 *
 * If this tab was just sent back and landed here again, Rails still cannot see
 * the session; resuming again would loop, so this declines and they land on
 * their dashboard instead.
 */
export const rememberOauthRedirectUnlessLooping = (value) => {
  try {
    const age =
      Date.now() - Number(window.sessionStorage.getItem(RESUMED_AT_KEY));
    if (age >= 0 && age < RESUME_LOOP_WINDOW_MS) return false;
  } catch {
    // No guard without storage.
  }
  return rememberOauthRedirect(value);
};

/** Pending Studio authorize path from sign-in/sign-up URL ?redirect_to=, if any. */
export const studioOauthResumeFromSearchParams = (searchParams) => {
  const redirectTo = searchParams?.get?.('redirect_to');
  if (!redirectTo || !isStudioOauthResume(redirectTo)) return null;
  return redirectTo;
};
